import React, { useCallback, useEffect, useState } from "react";
import { FileSystemItem } from "types";
import { BrainCircuit, Download, RefreshCw } from "lucide-react";
import { formatFileSize } from "utils";
import Card from "components/card";
import Loader from "components/Loader";
import toast from "react-hot-toast";
import api from "api/axios";
import { useTranslation } from "hooks/useTranslation";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";

const FileView = ({
  file,
  page = 1,
}: {
  file: FileSystemItem;
  page?: number;
}) => {
  const { t } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isPdf = file?.document_metadata?.file_type === "pdf";
  const [, setSearchParams] = useSearchParams();

  const fetchPdf = useCallback(
    async (abortController?: AbortController) => {
      if (!file || !isPdf) {
        setPdfUrl(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`/documents/${file.id}/download`, {
          responseType: "blob",
          signal: abortController?.signal,
        });

        const blob = response.data;
        const forcedBlob = new Blob([blob], { type: "application/pdf" });
        const url = URL.createObjectURL(forcedBlob);
        setPdfUrl(url);
      } catch (err: any) {
        if (err.name === "CanceledError") {
          // Request was cancelled, don't show error
          return;
        }
        setError(t("library.failedToLoadPDF"));
        console.error("PDF fetch error:", err);
      } finally {
        setLoading(false);
      }
    },
    [file, isPdf]
  );

  const handleRetry = () => {
    fetchPdf();
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchPdf(abortController);

    return () => {
      abortController.abort();
      setPdfUrl(null);
    };
  }, [file, fetchPdf]);

  const handleDownload = async () => {
    if (!file) return;

    try {
      const response = await api.get(`/documents/${file.id}/download`, {
        responseType: "blob",
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.target = "_blank";
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("library.failedToDownload"));
    }
  };

  if (!file) {
    return null;
  }

  return (
    <div className="mx-auto grid h-screen  max-h-[93vh]  min-w-[79vw] grid-cols-1 gap-6   2xl:grid-cols-3">
      {/* Left: File Preview (iframe) */}
      <div className="flex h-full min-h-[400px] w-full items-center justify-center rounded-lg  border-none 2xl:col-span-2">
        {isPdf ? (
          loading ? (
            <Loader txt={t("library.loadingPDFPreview")} />
          ) : error ? (
            <div className="flex flex-col items-center justify-center space-y-4">
              <span className="text-center text-red-500 dark:text-red-400">
                {error}
              </span>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-blue-700 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700 dark:hover:text-blue-400"
              >
                <RefreshCw className="h-4 w-4" />
                <span>{t("library.retry")}</span>
              </button>
            </div>
          ) : pdfUrl ? (
            <div className="h-full w-full  ">
              <iframe
                src={`${pdfUrl}#page=${page}`}
                title={t("library.filePreview")}
                className="h-[90vh] min-h-[400px] w-full rounded-lg border-none dark:invert"
              />
            </div>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">
              {t("library.noPreviewAvailable")}
            </span>
          )
        ) : (
          <span className="text-gray-500 dark:text-gray-400">
            {t("library.previewNotAvailable")}
          </span>
        )}
      </div>
      {/* Right: File Details */}
      <Card extra="flex w-full  max-h-max flex-col p-6 ">
        <div className="space-y-3">
          <div className="flex justify-between">
            <p className="break- text-gray-600 dark:text-gray-300">
              {t("library.fileName")}
            </p>
            <p className="w-full max-w-[70%] break-words break-all text-end font-medium dark:text-white  ">
              {file.name}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-gray-600 dark:text-gray-300">{t("library.fileSize")}</p>
            <p className="break-words break-all font-medium dark:text-white">
              {formatFileSize(file.document_metadata?.file_size ?? 0)}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-gray-600 dark:text-gray-300">{t("library.fileType")}</p>
            <p className="break-words break-all font-medium dark:text-white">
              {file.document_metadata?.file_type ?? ""}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              className={cn(
                "inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-blue-700 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700 dark:hover:text-blue-400",
                { hidden: !isPdf }
              )}
              onClick={() => setSearchParams({ deepExtract: file.id })}
            >
              <BrainCircuit className="h-4 w-4" />
              <span>Deep Extract</span>
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-blue-700 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700 dark:hover:text-blue-400"
              onClick={handleDownload}
            >
              <Download className="h-4 w-4" />
              <span>{t("library.download")}</span>
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FileView;
